import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { List, X } from "@phosphor-icons/react";

const links = [
  { to: "/", label: "Home", testid: "nav-home-link" },
  { to: "/services", label: "Services", testid: "nav-services-link" },
  { to: "/about", label: "About", testid: "nav-about-link" },
  { to: "/contact", label: "Contact", testid: "nav-contact-link" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-black/10" : "bg-white border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 group">
            <span className="relative inline-block w-9 h-9">
              <span className="absolute inset-0 bg-[#0A0A0A] group-hover:bg-[#FF4F00] transition-colors" />
              <span className="absolute inset-1.5 border border-white" />
              <span className="absolute top-1 left-1 w-2 h-2 bg-[#FF4F00] group-hover:bg-white transition-colors" />
            </span>
            <span className="font-display text-xl font-extrabold tracking-tight uppercase">
              UCUBE<span className="text-[#FF4F00]">.</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `text-xs font-bold uppercase tracking-[0.2em] link-underline ${
                    isActive ? "text-[#FF4F00]" : "text-[#0A0A0A] hover:text-[#FF4F00]"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:block">
            <Link
              to="/contact"
              data-testid="nav-cta-button"
              className="bg-[#0A0A0A] text-white px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#FF4F00] inline-flex items-center gap-2"
            >
              Start a Project
              <span aria-hidden>→</span>
            </Link>
          </div>

          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            className="md:hidden p-2 -mr-2 text-[#0A0A0A]"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} weight="bold" /> : <List size={24} weight="bold" />}
          </button>
        </div>

        {open && (
          <div data-testid="nav-mobile-menu" className="md:hidden pb-6 border-t border-black/10 pt-4">
            <div className="flex flex-col gap-4">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-${l.testid}`}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `text-sm font-bold uppercase tracking-[0.2em] ${
                      isActive ? "text-[#FF4F00]" : "text-[#0A0A0A]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-nav-cta-button"
                className="bg-[#0A0A0A] text-white px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] inline-block mt-2 text-center"
              >
                Start a Project →
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
